/*
  ============================================================
  cart-upsell.js — Carrossel de upsell do cart drawer
  ------------------------------------------------------------
  - Reposiciona <cart-upsell> pra DENTRO da área rolável
    (.cart-items), como irmã de <cart-items-list> — fica logo
    após o último item e rola junto com a lista.
  - Busca recomendações (intent=related) do 1º item do carrinho
    via Section Rendering API e injeta os cards (.cu-card) no
    trilho — mesmo mecanismo do bloco nativo _cart-related.
  - Esconde produtos que já estão no carrinho (por product_id e
    variant_id). Ao mudar o carrinho, re-filtra e, se o 1º item
    trocar de produto, rebusca as recomendações.
  - Botões ‹ › rolam o trilho; ficam desabilitados nos extremos.
  O "+" de cada card é o <buy-button> nativo do tema (add + refresh).
  ============================================================
*/
(function () {
  'use strict';
  if (window.__cartUpsellLoaded) return;
  window.__cartUpsellLoaded = true;

  var LOG = '[Upsell]';
  var LIMIT = 10;
  // header interno do cart-gifts: faz o gancho de fetch dele ignorar
  // nossas leituras de /cart.js (evita reconciliações desnecessárias).
  var INTERNAL = { 'x-cart-gifts-internal': '1' };
  // mesmas chaves de propriedade que cart-auto-gift-v4.js usa para
  // reconhecer linha de brinde (Cortesia: <valor> ou Brinde: <tier> da
  // régua) — uma linha de brinde nunca deve virar âncora de recomendação.
  var GIFT_PROPS = ['Cortesia', 'Brinde'];

  function toSet(attr) {
    return new Set(String(attr || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean));
  }
  function isGiftLine(item) {
    return !!(item.properties && GIFT_PROPS.some(function (k) { return item.properties[k] != null; }));
  }

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.track = this.querySelector('[data-cu-track]');
      this.prevBtn = this.querySelector('[data-cu-prev]');
      this.nextBtn = this.querySelector('[data-cu-next]');
      this.sectionId = this.getAttribute('data-section-id') || 'cart-upsell-products';

      this.productId = (this.getAttribute('data-product-id') || '').trim();
      this.inCartProducts = toSet(this.getAttribute('data-in-cart'));
      this.inCartVariants = toSet(this.getAttribute('data-in-cart-variants'));

      this._loadToken = 0;
      this._syncTimer = null;

      this.relocate();
      this.bindNav();
      this.bindTracking();
      this.observeCart();
      this.deferLoadToFirstOpen();
    }

    // A.056: adia a 1ª busca de recomendações para quando o drawer abrir de
    // fato — antes disparava no carregamento da página com o drawer fechado.
    deferLoadToFirstOpen() {
      var self = this;
      var loaded = false;
      function tryObserve() {
        var drawer = document.querySelector('drawer-component[data-drawer="cart"]');
        if (!drawer) return false;
        function check() {
          if (loaded || drawer.getAttribute('data-state') !== 'open') return;
          loaded = true;
          self.load(self.productId);
        }
        new MutationObserver(check).observe(drawer, { attributes: true, attributeFilter: ['data-state'] });
        check(); // cobre o caso raro do drawer já estar aberto neste ponto
        return true;
      }
      if (tryObserve()) return;
      var tries = 0;
      var iv = setInterval(function () {
        if (tryObserve() || ++tries > 40) clearInterval(iv);
      }, 100);
    }

    // Move a faixa pra dentro de .cart-items (o wrapper flex:1+overflow-y:auto
    // que rola) — como IRMÃ de <cart-items-list>, nunca como filha dela. O
    // custom element nativo (cart-items-list) só faz appendChild/innerHTML=""
    // nos PRÓPRIOS filhos (cart-item); ficando um nível acima, a cada add/
    // remove de item ele nunca disputa posição com a faixa nem a apaga num
    // reset. Resultado: acompanha a rolagem, sempre depois do último item.
    relocate() {
      try {
        var items = document.querySelector('cart-component .cart-items');
        if (items && this.parentNode !== items) {
          items.appendChild(this); // fim da área rolável, junto aos itens
        }
      } catch (e) { console.warn(LOG, 'relocate falhou:', e); }
    }

    // ---------- Recomendações ----------
    load(productId) {
      if (!productId) { this.hideStrip(); return; }
      var self = this;
      var token = ++this._loadToken;
      this._fetchedFor = productId; // marca a intenção (evita refetch duplo no sync)
      var url = '/recommendations/products?section_id=' + encodeURIComponent(this.sectionId) +
        '&product_id=' + encodeURIComponent(productId) +
        '&intent=related&limit=' + LIMIT;
      fetch(url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (token !== self._loadToken) return; // resposta obsoleta
          var cards = [];
          try {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            cards = Array.prototype.slice.call(doc.querySelectorAll('.cu-card'));
          } catch (e) { /* html vazio/ruim */ }
          self.track.innerHTML = '';
          cards.forEach(function (c) { self.track.appendChild(c); });
          self.track.scrollLeft = 0; // recarregou o conjunto: volta ao início
          self.applyFilter();
        })
        .catch(function (e) { if (token === self._loadToken) { console.warn(LOG, 'fetch falhou:', e); self.hideStrip(); } });
    }

    // Esconde cards de produtos já no carrinho; revela a faixa se sobrar
    // ao menos um card. Atualiza os botões de navegação.
    applyFilter() {
      if (!this.track) return;
      var self = this;
      var visible = 0;
      this.track.querySelectorAll('.cu-card').forEach(function (card) {
        var pid = card.getAttribute('data-product-id');
        var vid = card.getAttribute('data-variant-id');
        var inCart = (pid && self.inCartProducts.has(pid)) || (vid && self.inCartVariants.has(vid));
        card.hidden = !!inCart;
        if (!inCart) visible++;
      });
      if (visible > 0) {
        this.hidden = false;
        this.updateNav();
      } else {
        this.hideStrip();
      }
    }

    hideStrip() { this.hidden = true; }

    // ---------- GA4: rastreio do "+" (mede quanto o upsell agrega) ----------
    // Dispara no clique — mesmo instante do add otimista do <buy-button>
    // nativo (bundle minificado não expõe callback de sucesso pra
    // confirmar o add real). Chama window.gtag diretamente: esta loja usa
    // o gtag.js nativo da Shopify (confirmado via dataLayer real em
    // 03/set/2026), não GTM — um dataLayer.push em formato de objeto não
    // seria consumido por ninguém aqui.
    // item_id replica o padrão shopify_ZZ_{product_id}_{variant_id} que os
    // eventos nativos da Shopify já usam nesta loja, pra não fragmentar os
    // relatórios por item no GA4 (mesmo produto, id diferente = item novo).
    // Nota: como o add nativo também dispara seu próprio add_to_cart pro
    // mesmo item, o total agregado de "Add to carts" no GA4 fica com
    // dupla contagem nessas ações — não afeta o filtro por item_list_name.
    bindTracking() {
      var self = this;
      if (!this.track) return;
      this.track.addEventListener('click', function (e) {
        var btn = e.target.closest('.cu-card__add');
        if (btn && self.track.contains(btn)) self.trackAddToCart(btn);
      });
    }

    trackAddToCart(btn) {
      if (typeof window.gtag !== 'function') return;
      try {
        var cents = parseFloat(btn.getAttribute('data-price')) || 0;
        var price = Math.round(cents) / 100;
        var productId = btn.getAttribute('data-product-id') || '';
        var variantId = btn.getAttribute('data-variant-id') || '';
        var item = {
          id: 'shopify_ZZ_' + productId + '_' + variantId,
          name: btn.getAttribute('data-product-title') || '',
          variant: btn.getAttribute('data-variant-title') || '',
          price: price,
          quantity: 1,
          item_list_id: 'cart_upsell',
          item_list_name: 'cart_upsell'
        };
        window.gtag('event', 'add_to_cart', { currency: 'BRL', value: price, items: [item] });
      } catch (e) { console.warn(LOG, 'tracking falhou:', e); }
    }

    // ---------- Navegação (carrossel) ----------
    bindNav() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.scrollByPage(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.scrollByPage(1); });
      if (this.track) {
        this.track.addEventListener('scroll', function () {
          if (self._navRaf) return;
          self._navRaf = requestAnimationFrame(function () { self._navRaf = 0; self.updateNav(); });
        }, { passive: true });
      }
      if (typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(function () { self.updateNav(); });
        if (this.track) this._ro.observe(this.track);
      }
    }

    scrollByPage(dir) {
      if (!this.track) return;
      var firstCard = this.track.querySelector('.cu-card:not([hidden])');
      var step = firstCard ? (firstCard.offsetWidth + 10) : Math.round(this.track.clientWidth * 0.8);
      this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    updateNav() {
      if (!this.track || !this.prevBtn || !this.nextBtn) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var x = this.track.scrollLeft;
      var overflow = max > 2;
      this.prevBtn.disabled = !overflow || x <= 1;
      this.nextBtn.disabled = !overflow || x >= max - 1;
    }

    // ---------- Sincronização com o carrinho ----------
    // 2.2: em vez de um MutationObserver + fetch('/cart.js') próprios a
    // cada mudança do carrinho, assina o broadcast que o cart-extras já
    // mantém (todo módulo de brinde entrega o carrinho reconciliado ali).
    // Sem cart-extras (não deveria acontecer no contexto atual do drawer),
    // cai no observer+fetch de antes.
    observeCart() {
      var self = this;
      if (window.__cartExtrasSubscribe) {
        window.__cartExtrasSubscribe(function (cart) { self.syncCart(cart); });
        return;
      }
      var list = document.querySelector('cart-component cart-items-list')
        || document.querySelector('cart-component .cart-items--list')
        || document.querySelector('cart-component .cart-items');
      if (!list || typeof MutationObserver === 'undefined') return;
      new MutationObserver(function () {
        clearTimeout(self._syncTimer);
        self._syncTimer = setTimeout(function () { self.fetchAndSyncCart(); }, 250);
      }).observe(list, { childList: true, subtree: true });
    }

    fetchAndSyncCart() {
      var self = this;
      fetch('/cart.js', { headers: INTERNAL })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) self.syncCart(cart); })
        .catch(function () { /* silencioso */ });
    }

    syncCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      var self = this;
      self.inCartProducts = new Set(cart.items.map(function (i) { return String(i.product_id); }));
      self.inCartVariants = new Set(cart.items.map(function (i) { return String(i.variant_id); }));

      if (!cart.items.length) { self.hideStrip(); return; }

      // A.056: âncora das recomendações é o 1º item PAGO — uma linha de
      // brinde da régua/gatilho nunca deve decidir o que é sugerido.
      var anchor = cart.items.filter(function (i) { return !isGiftLine(i); })[0];
      if (!anchor) { self.hideStrip(); return; }

      var firstPid = String(anchor.product_id);
      if (firstPid !== self._fetchedFor) {
        self.productId = firstPid;
        self.load(firstPid); // rebusca recomendações p/ o novo 1º item
      } else {
        self.applyFilter(); // só re-filtra (item adicionado/removido)
      }
    }
  }

  if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
})();
